/**
 * Values that cannot be CSS custom properties.
 *
 * Everything a screen paints comes from `theme.css`. What lives here is the
 * data-driven half: colours chosen per *record* (an appointment type, a
 * contract type) and read by canvas/SVG code that cannot resolve `var()`.
 */

export const DENSITIES = ['comfortable', 'compact'] as const;
export type Density = (typeof DENSITIES)[number];

export const THEMES = ['light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

/** A colour that has to work on both token sets. */
export interface DataColor {
  readonly light: string;
  readonly dark: string;
}

/**
 * Categorical scale for record-level colour: appointment types (`tipo_appuntamento`
 * carries a colour in the lookup table), contract type on portfolio breakdowns,
 * activity kinds on the timeline.
 *
 * Six hues at roughly constant chroma and lightness, starting from the brand
 * terracotta. Ordered by how often a category appears, so the first two entries
 * are the ones a user sees most.
 *
 * Provisional: validated by eye on the mockups, not against a contrast checker
 * on real charts. Re-check when the dashboards get actual series.
 */
export const DATA_COLORS = {
  terracotta: { light: '#b4552f', dark: '#e0764a' },
  ochre: { light: '#a97b2c', dark: '#d9a656' },
  olive: { light: '#3f6b3a', dark: '#86bb7f' },
  slate: { light: '#4d6a8a', dark: '#8fadcd' },
  plum: { light: '#7a4a6a', dark: '#c08ab0' },
  clay: { light: '#8a6a5b', dark: '#c4a08c' },
} as const satisfies Record<string, DataColor>;

export type DataColorName = keyof typeof DATA_COLORS;

export const DATA_COLOR_ORDER = Object.keys(DATA_COLORS) as readonly DataColorName[];

/** Reached only if the scale is empty — the type allows what the data does not. */
const FALLBACK_COLOR: DataColorName = 'terracotta';

/** Resolve a categorical colour for the active theme. */
export function dataColor(name: DataColorName, theme: Theme): string {
  return DATA_COLORS[name][theme];
}

/**
 * Stable colour for a category with no colour of its own — same input always
 * yields the same hue, so a series does not re-colour between renders.
 */
export function dataColorFor(key: string, theme: Theme): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const name = DATA_COLOR_ORDER[Math.abs(hash) % DATA_COLOR_ORDER.length] ?? FALLBACK_COLOR;
  return dataColor(name, theme);
}
